import { NotificationsObserver } from '../notifications-observer/implementation';
import { ObserverUnobserveOne } from '../../../core/observer/implementation';
import { GetSetSuperArgsFunction, IsFactoryClass, MakeFactory } from '../../../classes/class-helpers/factory';
import { BaseClass } from '../../../classes/class-helpers/base-class';
import { IS_OBSERVABLE_LIKE_CONSTRUCTOR, IsObservableLikeConstructor } from '../../../core/observable/constructor';
import { ObservableFactory } from '../../../core/observable/implementation';
import { NOTIFICATIONS_OBSERVABLE_PRIVATE } from './privates';
import { ConstructNotificationsObservable, IS_NOTIFICATIONS_OBSERVABLE_CONSTRUCTOR } from './constructor';
import { NormalizeNotificationsObservableMatchOptions } from './functions';
import { IsNotificationsObserver } from '../notifications-observer/constructor';
export function NotificationsObservableOnObserved(instance, observer) {
    const privates = instance[NOTIFICATIONS_OBSERVABLE_PRIVATE];
    if (IsNotificationsObserver(observer)) {
        const name = observer.name;
        if (!privates.observersMap.has(name)) {
            privates.observersMap.set(name, []);
        }
        privates.observersMap.get(name).push(observer);
    }
    else {
        privates.othersObservers.push(observer);
    }
    privates.onObserveHook(observer);
}
export function NotificationsObservableOnUnobserved(instance, observer) {
    const privates = instance[NOTIFICATIONS_OBSERVABLE_PRIVATE];
    if (IsNotificationsObserver(observer)) {
        const name = observer.name;
        const observers = privates.observersMap.get(name);
        observers.splice(observers.indexOf(observer), 1);
        if (observers.length === 0) {
            privates.observersMap.delete(name);
        }
    }
    else {
        privates.othersObservers.splice(privates.othersObservers.indexOf(observer), 1);
    }
    privates.onUnobserveHook(observer);
}
export function NotificationsObservableAddListener(instance, name, callback) {
    return new NotificationsObserver(name, callback).observe(instance);
}
export function NotificationsObservableRemoveListener(instance, name, callback) {
    const observers = Array.from(NotificationsObservableMatches(instance, name, callback));
    for (let i = 0, l = observers.length; i < l; i++) {
        ObserverUnobserveOne(observers[i], instance);
    }
}
export function NotificationsObservableHasListener(instance, name, callback, options) {
    const _options = NormalizeNotificationsObservableMatchOptions(options);
    const privates = instance[NOTIFICATIONS_OBSERVABLE_PRIVATE];
    if (_options.includeGlobalObservers
        && (privates.othersObservers.length > 0)) {
        return true;
    }
    else if (privates.observersMap.has(name)) {
        const observers = privates.observersMap.get(name);
        if (callback === void 0) {
            return observers.length > 0;
        }
        else {
            for (let i = 0, l = observers.length; i < l; i++) {
                if (observers[i].callback === callback) {
                    return true;
                }
            }
            return false;
        }
    }
    else {
        return false;
    }
}
export function* NotificationsObservableMatches(instance, name, callback, options) {
    const _options = NormalizeNotificationsObservableMatchOptions(options);
    const privates = instance[NOTIFICATIONS_OBSERVABLE_PRIVATE];
    if (privates.observersMap.has(name)) {
        const observers = privates.observersMap.get(name);
        if (callback === void 0) {
            yield* observers;
        }
        else {
            for (let i = 0, l = observers.length; i < l; i++) {
                if (observers[i].callback === callback) {
                    yield observers[i];
                }
            }
        }
    }
    if (_options.includeGlobalObservers) {
        yield* privates.othersObservers;
    }
}
function PureNotificationsObservableFactory(superClass) {
    if (!IsObservableLikeConstructor(superClass)) {
        throw new TypeError(`Expected Observables' constructor as superClass`);
    }
    const setSuperArgs = GetSetSuperArgsFunction(IsFactoryClass(superClass));
    return class NotificationsObservable extends superClass {
        constructor(...args) {
            const [create] = args[0];
            let context;
            super(...setSuperArgs(args.slice(1), [
                (_context) => {
                    context = _context;
                    return {
                        onObserved: (observer) => {
                            NotificationsObservableOnObserved(this, observer);
                        },
                        onUnobserved: (observer) => {
                            NotificationsObservableOnUnobserved(this, observer);
                        }
                    };
                }
            ]));
            ConstructNotificationsObservable(this, context, create);
        }
        addListener(name, callback) {
            return NotificationsObservableAddListener(this, name, callback);
        }
        removeListener(name, callback) {
            NotificationsObservableRemoveListener(this, name, callback);
        }
        on(name, callback) {
            this.addListener(name, callback).activate();
            return this;
        }
        off(name, callback) {
            this.removeListener(name, callback);
            return this;
        }
        hasListener(name, callback, options) {
            return NotificationsObservableHasListener(this, name, callback, options);
        }
        matches(name, callback, options) {
            return NotificationsObservableMatches(this, name, callback, options);
        }
    };
}
export let NotificationsObservable;
export function NotificationsObservableFactory(superClass) {
    return MakeFactory(PureNotificationsObservableFactory, [], superClass, {
        name: 'NotificationsObservable',
        instanceOf: NotificationsObservable,
        waterMarks: [IS_NOTIFICATIONS_OBSERVABLE_CONSTRUCTOR, IS_OBSERVABLE_LIKE_CONSTRUCTOR],
    });
}
export function NotificationsObservableBaseFactory(superClass) {
    return MakeFactory(PureNotificationsObservableFactory, [ObservableFactory], superClass, {
        name: 'NotificationsObservable',
        instanceOf: NotificationsObservable,
        waterMarks: [IS_NOTIFICATIONS_OBSERVABLE_CONSTRUCTOR, IS_OBSERVABLE_LIKE_CONSTRUCTOR],
    });
}
NotificationsObservable = class NotificationsObservable extends NotificationsObservableBaseFactory(BaseClass) {
    constructor(create) {
        super([create], []);
    }
};
//# sourceMappingURL=implementation.js.map